import { useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import slackLogo from '@/assets/integrations/slack.svg';
import { LoginPage } from '@/features/auth/LoginPage';
import { connectSlack } from '@/services/api';
import { createSlackConnect, SLACK_CONNECT_FLOW_ID, type SlackConnectState } from './slackConnect';

/**
 * `/connect/slack`: the login frame running the Slack connect flow (sign in, then
 * approve Elliot in Slack). When the flow ends, tell the server and show how it went
 * in place of the flow.
 */
export function SlackConnectPage() {
  const [state, setState] = useState<SlackConnectState>('flow');
  // Created once: it holds the "attempt in flight" flag across renders. The flag is
  // defensive — the flow unmounts as soon as the state leaves 'flow'.
  const [sc] = useState(() => createSlackConnect(connectSlack, setState));

  let outcome = null;
  if (state === 'connecting') outcome = <CircularProgress sx={{ alignSelf: 'center' }} />;
  else if (state !== 'flow' && 'connected' in state)
    outcome = (
      <Stack spacing={2} alignItems="center">
        {/* The name is the Slack workspace's, not the Elliot organization's — they
            often match (it read like "Connected to <our own tenant>"), so say which. */}
        <Typography>Connected to the {state.connected} Slack workspace</Typography>
        <Button variant="contained" onClick={() => window.location.assign('/')}>
          Open Elliot
        </Button>
      </Stack>
    );
  else if (state !== 'flow')
    outcome = (
      <Stack spacing={2} alignItems="center">
        <Typography sx={{ textAlign: 'center' }}>{state.error}</Typography>
        <Button variant="outlined" onClick={sc.retry}>
          Try again
        </Button>
      </Stack>
    );

  return (
    <LoginPage
      flowId={SLACK_CONNECT_FLOW_ID}
      title="Connect Elliot to Slack"
      subtitle="Sign in, then approve Elliot in your Slack workspace"
      // Same mark and size as Slack's tile on the Integrations screen.
      mark={<Box component="img" src={slackLogo} alt="Slack" sx={{ width: 40, height: 40, mb: 1.5 }} />}
      onSuccess={sc.onSuccess}
    >
      {outcome}
    </LoginPage>
  );
}
